import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { fetchStorageQuota } from './lib/api';
import { subscribeStorageChanges } from './lib/localData';
import { pushToast } from './lib/toast';
import { useOnlineState } from './hooks/useOnlineState';
import { AppShell } from './components/AppShell';

const DashboardPage = lazy(() => import('./routes/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const JobsPage = lazy(() => import('./routes/JobsPage').then((module) => ({ default: module.JobsPage })));
const IntakePage = lazy(() => import('./routes/IntakePage').then((module) => ({ default: module.IntakePage })));
const LibraryPage = lazy(() => import('./routes/LibraryPage').then((module) => ({ default: module.LibraryPage })));
const IncidentsPage = lazy(() => import('./routes/IncidentsPage').then((module) => ({ default: module.IncidentsPage })));
const SettingsPage = lazy(() => import('./routes/SettingsPage').then((module) => ({ default: module.SettingsPage })));

export function RoutedApp() {
  const online = useOnlineState();
  const [syncState, setSyncState] = useState({
    flushing: false,
    lastRunAt: new Date().toISOString(),
    lastResult: 'Local device storage active'
  });
  const lastWarningRef = useRef('');

  useEffect(() => {
    setSyncState({
      flushing: false,
      lastRunAt: new Date().toISOString(),
      lastResult: online ? 'Offline-ready workspace' : 'Working from device storage'
    });
  }, [online]);

  useEffect(() => {
    const checkQuota = async (): Promise<void> => {
      const quota = await fetchStorageQuota();
      if (!quota.nearingLimit) return;
      const signature = `${quota.percent}`;
      if (lastWarningRef.current === signature) return;
      lastWarningRef.current = signature;
      pushToast(`Storage ${quota.percent}% full. Export or remove photos soon.`, 'warn', 5000);
    };

    void checkQuota();
    return subscribeStorageChanges(() => {
      void checkQuota();
    });
  }, []);

  return (
    <BrowserRouter>
      <AppShell online={online} syncState={syncState}>
        <Suspense fallback={<div className="route-loading card">Loading workspace…</div>}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/jobs/:jobId" element={<JobsPage />} />
            <Route path="/intake" element={<IntakePage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </Suspense>
      </AppShell>
    </BrowserRouter>
  );
}
